import {
  Stack,
  Group,
  ActionIcon,
  Text,
  InputWrapper,
  Select,
  MultiSelect,
  Button,
  Title,
  Loader,
  Space,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import {
  IconArrowLeft,
  IconArrowRight,
  IconCloudX,
  IconRefresh,
} from "@tabler/icons-react"
import { useState } from "react"

import { BQErrorCode } from "@costory/types/endpoints/billingDatasources"
import {
  BQDatasourceFormData,
  CSVOrientation,
} from "@costory/types/endpoints/metricsDatasources"
import { MAPPING_BQ_TYPES } from "@costory/types/endpoints/shared"
import { MetricsDatasourceType } from "@costory/types/prisma-client"

import { BigQueryErrorDisplay } from "@costory/front/components/BigQueryErrorDisplay"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { queryClient } from "@costory/front/lib/queryClient"
import { useValidateBigQuery } from "@costory/front/queries/metricsDatasources"

const authorizedTypes = {
  metricNameColumn: MAPPING_BQ_TYPES.STRING,
  metricValueColumn: MAPPING_BQ_TYPES.NUMERIC,
  metricsColumns: MAPPING_BQ_TYPES.NUMERIC,
  dateColumn: MAPPING_BQ_TYPES.DATE,
}

export const BigQueryValidation = ({
  form,
  handleClickBack,
  handleClickNext,
}: {
  form: UseFormReturnType<BQDatasourceFormData>
  handleClickBack: () => void
  handleClickNext: () => void
}) => {
  const query = useValidateBigQuery({
    bqUri: form.getValues().bqUri,
    name: form.getValues().name,
    type: MetricsDatasourceType.BigQuery,
  })
  form.watch("orientationTable", () => {
    form.setFieldValue("metricNameColumn", undefined)
    form.setFieldValue("metricValueColumn", undefined)
    form.setFieldValue("metricsColumns", undefined)
  })
  const [customValidation, setCustomValidation] = useState(false)
  const callCustomValidation = () => {
    const values = form.getValues()
    if (values.orientationTable === CSVOrientation.Rows) {
      return !!values.metricNameColumn && !!values.metricValueColumn
    }
    return !!values.metricsColumns && values.metricsColumns.length > 0
  }
  form.watch("metricNameColumn", () => {
    setCustomValidation(callCustomValidation())
  })
  form.watch("metricsColumns", () => {
    setCustomValidation(callCustomValidation())
  })
  form.watch("metricValueColumn", () => {
    setCustomValidation(callCustomValidation())
  })
  return (
    <Stack justify="space-between" h="100%">
      <Stack flex={1}>
        <QueryWrapper
          query={query}
          LoadingComponent={
            <Stack align="center">
              <Loader size="xl" />
              <Title order={2}>Validating datasource...</Title>
            </Stack>
          }
        >
          {({ data }) => {
            if (data?.isSuccess) {
              return (
                <Stack w="100%">
                  <InputWrapper label="Select Orientation">
                    <Select
                      {...form.getInputProps("orientationTable")}
                      data={[CSVOrientation.Rows, CSVOrientation.Columns]}
                      data-intercom-target="costory-tour-datasource-bigquery-orientation-select"
                    />
                  </InputWrapper>
                  <InputWrapper label="Select date column">
                    <Select
                      data={data.schema?.fields
                        .filter((el) =>
                          authorizedTypes.dateColumn.includes(el.type),
                        )
                        .map(({ name }) => name)}
                      {...form.getInputProps("dateColumn")}
                      data-intercom-target="costory-tour-datasource-bigquery-date-column-select"
                    />
                  </InputWrapper>
                  {form.getValues().orientationTable ===
                    CSVOrientation.Rows && (
                    <Stack>
                      <InputWrapper label="Select metricName column">
                        <Select
                          data={data.schema?.fields
                            .filter((el) =>
                              authorizedTypes.metricNameColumn.includes(
                                el.type,
                              ),
                            )
                            .map(({ name }) => name)}
                          {...form.getInputProps("metricNameColumn")}
                          data-intercom-target="costory-tour-datasource-bigquery-metric-name-select"
                        />
                      </InputWrapper>
                      <InputWrapper label="Select metricValue column">
                        <Select
                          data={data.schema?.fields
                            .filter((el) =>
                              authorizedTypes.metricValueColumn.includes(
                                el.type,
                              ),
                            )
                            .map(({ name }) => name)}
                          {...form.getInputProps("metricValueColumn")}
                          data-intercom-target="costory-tour-datasource-bigquery-metric-value-select"
                        />
                      </InputWrapper>
                    </Stack>
                  )}
                  {form.getValues().orientationTable ===
                    CSVOrientation.Columns && (
                    <Stack>
                      <InputWrapper label="Select metrics columns">
                        <MultiSelect
                          {...form.getInputProps("metricsColumns")}
                          data={data.schema?.fields
                            .filter((el) =>
                              authorizedTypes.metricsColumns.includes(el.type),
                            )
                            .map(({ name }) => name)}
                          data-intercom-target="costory-tour-datasource-bigquery-metrics-columns-select"
                        />
                      </InputWrapper>
                    </Stack>
                  )}
                </Stack>
              )
            }
            return (
              <Stack align="center" justify="center" flex={1}>
                <IconCloudX color="textSecondary" size={120} />
                <Title order={2}>Your datasource is invalid</Title>
                <BigQueryErrorDisplay
                  errorCode={query.data?.errorCode as BQErrorCode}
                  tablePath={form.getValues().bqUri}
                />

                <Space h={10} />
                <Group justify="flex-end">
                  <Text>Retry</Text>
                  <ActionIcon
                    loading={query.isLoading}
                    onClick={() =>
                      queryClient.resetQueries({
                        queryKey: ["validateBigQuery"],
                      })
                    }
                    data-intercom-target="costory-tour-datasource-bigquery-refresh-button"
                  >
                    <IconRefresh />
                  </ActionIcon>
                </Group>
              </Stack>
            )
          }}
        </QueryWrapper>
      </Stack>
      <Group justify="space-between" pb={20}>
        <Button
          onClick={handleClickBack}
          variant="outline"
          leftSection={<IconArrowLeft />}
          data-intercom-target="costory-tour-datasource-bigquery-back-button"
        >
          Back
        </Button>
        <Button
          onClick={handleClickNext}
          rightSection={<IconArrowRight />}
          disabled={!customValidation}
          data-intercom-target="costory-tour-datasource-bigquery-next-button"
        >
          Next
        </Button>
      </Group>
    </Stack>
  )
}
